// Startup resume alignment for seek-anchored hls.js sessions (#847).
//
// A seek-anchored hls.js session is served a ready-only window that begins at
// the anchor fragment, so media-element time zero is the anchor rather than the
// resume point (#842). The server rounds that anchor down to a segment boundary
// at or before the requested position, which leaves the resume point up to one
// segment inside the first fragment.
//
// Nothing ever moved the element across that remainder. The startup pending
// seek was left to expire on its own once ordinary playback drifted past the
// target, and until it did the player hid the video element and held the
// buffering overlay -- so the audio played through several seconds of content
// the viewer could not see. Decide the startup seek here instead, so picture
// and sound both begin at the resume point.

// Trailing margin kept away from a buffered range's end, so a target that lands
// on the boundary of the data hls.js has appended is treated as not yet ready
// rather than seeked into the gap behind it.
export const STARTUP_RESUME_SEEK_BUFFERED_SAFETY_MARGIN_SECONDS = 0.05;

export type StartupResumeBufferedRange = Readonly<{
  startSeconds: number;
  endSeconds: number;
}>;

export type StartupResumeSeekDecision =
  | { action: "none" }
  | { action: "wait_for_buffered_target" }
  | { action: "seek"; targetSeconds: number };

export function evaluateStartupResumeSeek(input: {
  pendingSeekAbsoluteSeconds: number | null;
  mediaTimelineOffsetSeconds: number;
  currentRelativeSeconds: number;
  reachedToleranceSeconds: number;
  segmentDurationSeconds: number;
  // Duration hls.js reports for the anchor fragment (the first fragment of
  // the loaded playlist). Copy-mode segments are keyframe-snapped, so the
  // anchor fragment can be well over the profile's nominal segment duration
  // (#965: 20.77 s against a nominal 12 s); when known it is the real bound
  // on how far into the session's window a genuine remainder can reach.
  anchorFragmentDurationSeconds?: number | null;
  bufferedRanges: readonly StartupResumeBufferedRange[];
}): StartupResumeSeekDecision {
  const {
    pendingSeekAbsoluteSeconds,
    mediaTimelineOffsetSeconds,
    currentRelativeSeconds,
    reachedToleranceSeconds,
    segmentDurationSeconds,
    anchorFragmentDurationSeconds = null,
    bufferedRanges,
  } = input;
  if (
    pendingSeekAbsoluteSeconds === null
    || !Number.isFinite(pendingSeekAbsoluteSeconds)
    || !Number.isFinite(mediaTimelineOffsetSeconds)
    || !Number.isFinite(currentRelativeSeconds)
  ) {
    return { action: "none" };
  }

  const targetSeconds = pendingSeekAbsoluteSeconds - mediaTimelineOffsetSeconds;
  // A remainder inside the reached tolerance already counts as arrived, so the
  // pending seek clears at the anchor without a seek costing a rebuffer.
  if (!Number.isFinite(targetSeconds) || targetSeconds <= reachedToleranceSeconds) {
    return { action: "none" };
  }
  // Only ever move forward into the session's own window. Playback that has
  // already reached the resume point needs no help, and a backward write here
  // would fight the anchor the server picked.
  if (currentRelativeSeconds >= targetSeconds - reachedToleranceSeconds) {
    return { action: "none" };
  }
  // The anchor is the segment boundary at or before the resume point, so a
  // genuine remainder is smaller than the anchor segment. Anything larger is
  // not this session's anchor arithmetic -- a zero origin on a profile whose
  // media clock already starts at the resume point reads that way -- and
  // seeking there would jump somewhere the viewer never asked for. The bound
  // is the anchor fragment's real duration when hls.js has reported it
  // (copy-mode fragments run past the nominal duration), else the nominal one.
  if (!Number.isFinite(segmentDurationSeconds) || segmentDurationSeconds <= 0) {
    return { action: "none" };
  }
  const remainderBoundSeconds =
    anchorFragmentDurationSeconds !== null
    && Number.isFinite(anchorFragmentDurationSeconds)
    && anchorFragmentDurationSeconds > 0
      ? Math.max(segmentDurationSeconds, anchorFragmentDurationSeconds)
      : segmentDurationSeconds;
  if (targetSeconds > remainderBoundSeconds) {
    return { action: "none" };
  }

  const containingRange = bufferedRanges.find((range) => {
    if (!Number.isFinite(range.startSeconds) || !Number.isFinite(range.endSeconds)) {
      return false;
    }
    const safeEnd = Math.max(
      range.startSeconds,
      range.endSeconds - STARTUP_RESUME_SEEK_BUFFERED_SAFETY_MARGIN_SECONDS,
    );
    return targetSeconds >= range.startSeconds && targetSeconds <= safeEnd;
  });
  if (!containingRange) {
    return { action: "wait_for_buffered_target" };
  }
  return { action: "seek", targetSeconds };
}

// bufferedStartupSeekTarget converts the pending absolute resume position into
// the element clock and reports it only when the element already holds that
// position, so a BUFFER_APPENDED handler can move the element before the first
// play(). pendingSeekTimeRef carries absolute title seconds while the element
// clock starts at the session anchor; comparing the absolute value against
// element-relative buffered ranges started playback one anchor origin late
// whenever the first window happened to cover it (#980: 15 s start on a 12 s
// origin played from 27 s, which is why the streaming gate's forward burst
// ran past the fixture end).
export function bufferedStartupSeekTarget(input: {
  pendingSeekAbsoluteSeconds: number | null;
  mediaTimelineOffsetSeconds: number;
  bufferedRanges: readonly StartupResumeBufferedRange[];
}): number | null {
  const { pendingSeekAbsoluteSeconds, mediaTimelineOffsetSeconds, bufferedRanges } = input;
  if (
    pendingSeekAbsoluteSeconds === null
    || !Number.isFinite(pendingSeekAbsoluteSeconds)
    || !Number.isFinite(mediaTimelineOffsetSeconds)
  ) {
    return null;
  }
  const targetSeconds = pendingSeekAbsoluteSeconds - mediaTimelineOffsetSeconds;
  if (!Number.isFinite(targetSeconds) || targetSeconds < 0) {
    return null;
  }
  const buffered = bufferedRanges.some((range) =>
    Number.isFinite(range.startSeconds)
    && Number.isFinite(range.endSeconds)
    && targetSeconds >= range.startSeconds
    && targetSeconds <= range.endSeconds);
  return buffered ? targetSeconds : null;
}
